// components/prediction/DailyPrediction.jsx
// Daily 5-tier prediction + Mood barometer
// "Train Your Market Sense" — direction cards with color + arrow icons

import React, { useState, useEffect } from 'react';
import { theme } from '../../theme';

const TIERS = [
  { id: 'big_rise',   label: 'Big Rise',   range: '≥ +5%',     icon: '⬆⬆', color: '#22a06b', bgTint: 'rgba(34, 160, 107, 0.10)', borderTint: 'rgba(34, 160, 107, 0.35)' },
  { id: 'small_rise', label: 'Small Rise',  range: '+1% ~ +5%', icon: '⬆',   color: '#57d9a3', bgTint: 'rgba(87, 217, 163, 0.08)', borderTint: 'rgba(87, 217, 163, 0.30)' },
  { id: 'flat',       label: 'Flat',        range: '-1% ~ +1%', icon: '→',   color: '#8b949e', bgTint: 'rgba(139, 148, 158, 0.08)', borderTint: 'rgba(139, 148, 158, 0.30)' },
  { id: 'small_drop', label: 'Small Drop',  range: '-1% ~ -5%', icon: '⬇',   color: '#e07a5f', bgTint: 'rgba(224, 122, 95, 0.08)',  borderTint: 'rgba(224, 122, 95, 0.30)' },
  { id: 'big_drop',   label: 'Big Drop',    range: '≤ -5%',     icon: '⬇⬇', color: '#cf4444', bgTint: 'rgba(207, 68, 68, 0.10)',   borderTint: 'rgba(207, 68, 68, 0.35)' },
];

const MOODS = [
  { id: 'calm',      label: 'Calm',      icon: '🧘', description: 'Clear-headed' },
  { id: 'confident', label: 'Confident', icon: '💪', description: 'Conviction' },
  { id: 'anxious',   label: 'Anxious',   icon: '😰', description: 'Uncertain' },
  { id: 'impulsive', label: 'Impulsive', icon: '⚡', description: 'FOMO' },
];

export function DailyPrediction({ ticker, onSubmit, alreadyPredicted, existingPrediction, aiTier, savedPick, savedPicks, onSavePick }) {
  const [selectedTier, setSelectedTier] = useState(existingPrediction?.tier || null);
  const [selectedMood, setSelectedMood] = useState(existingPrediction?.mood || null);
  const [submitted, setSubmitted] = useState(!!alreadyPredicted);

  // Sync with async props from Firebase
  useEffect(() => {
    if (alreadyPredicted) setSubmitted(true);
  }, [alreadyPredicted]);

  useEffect(() => {
    if (existingPrediction?.tier) setSelectedTier(existingPrediction.tier);
    if (existingPrediction?.mood) setSelectedMood(existingPrediction.mood);
  }, [existingPrediction]);

  const handleSubmit = () => {
    if (!selectedTier) return;
    setSubmitted(true);
    if (onSubmit) {
      onSubmit({
        ticker,
        tier: selectedTier,
        mood: selectedMood,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const tierObj = TIERS.find(t => t.id === selectedTier);
  const moodObj = MOODS.find(m => m.id === selectedMood);

  // Fetch top picks after prediction
  const initPicks = savedPicks ? { picks: savedPicks } : savedPick ? { picks: [savedPick] } : null;
  const [topPicks, setTopPicks] = useState(initPicks);
  const [topPicksLoading, setTopPicksLoading] = useState(false);

  // Sync topPicks when savedPick/savedPicks loads asynchronously
  useEffect(() => {
    if (savedPicks && savedPicks.length > 0 && !topPicks) {
      setTopPicks({ picks: savedPicks });
    } else if (savedPick && !topPicks) {
      setTopPicks({ picks: [savedPick] });
    }
  }, [savedPick, savedPicks]);

  useEffect(() => {
    if (submitted && selectedTier && !topPicks && !savedPick && !savedPicks) {
      setTopPicksLoading(true);
      fetch('https://insightstock-api-358482645476.us-west2.run.app/api/top-picks')
        .then(r => r.json())
        .then(data => {
          if (data.picks && data.picks.length > 0) {
            // Pick 1 random (fallback for users who haven't been allocated yet)
            const rand = data.picks[Math.floor(Math.random() * data.picks.length)];
            setTopPicks({ picks: [rand] });
            if (onSavePick) onSavePick([rand]);
          } else {
            setTopPicks(data);
          }
          setTopPicksLoading(false);
        })
        .catch(() => setTopPicksLoading(false));
    }
  }, [submitted, selectedTier, selectedMood]);

  // Already submitted state — instant feedback
  if (submitted && selectedTier) {
    const aiObj = TIERS.find(t => t.id === aiTier);
    const userAgreed = selectedTier === aiTier;
    const tierIndex = (t) => TIERS.findIndex(x => x.id === t);
    const userIdx = tierIndex(selectedTier);
    const aiIdx = tierIndex(aiTier);
    let comparison = '';
    if (userAgreed) comparison = `You and AI both say ${aiObj?.label}.`;
    else if (userIdx > aiIdx) comparison = `You're more bullish than AI.`;
    else comparison = `You're more bearish than AI.`;

    return (
      <div style={{ padding: '4px 0', textAlign: 'center' }}>
        {/* Instant feedback card */}
        <div style={{
          background: tierObj?.bgTint || theme.bg,
          borderRadius: '12px', padding: '20px',
          border: '2px solid ' + (tierObj?.color || theme.border),
          marginBottom: '12px',
        }}>
          <p style={{ color: tierObj?.color, fontSize: '26px', fontWeight: 700, margin: 0 }}>
            {tierObj?.icon} {tierObj?.label}
          </p>
        </div>

        {/* Comparison */}
        <div style={{
          background: 'rgba(45, 212, 191, 0.06)',
          border: '1px solid ' + theme.border,
          borderRadius: '10px', padding: '14px 18px', marginBottom: '12px',
        }}>
          <p style={{ color: theme.text, fontSize: '16px', fontWeight: 600, margin: '0 0 4px' }}>
            {comparison} {userAgreed ? "Let's see if you're both right." : "Let's see who wins."}
          </p>
          <p style={{ color: theme.textDark, fontSize: '14px', margin: 0 }}>
            {moodObj ? `${moodObj.icon} ${moodObj.label} — ` : ''}Check back tomorrow.
          </p>
        </div>

        <button
          onClick={() => setSubmitted(false)}
          style={{
            padding: '10px 28px', borderRadius: '10px',
            border: '1px solid ' + theme.border, background: 'transparent',
            color: theme.textMuted, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Change Prediction
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '4px 0' }}>

      {/* Prediction prompt */}
      <p style={{ color: theme.text, fontSize: '18px', fontWeight: 600, margin: '0 0 14px' }}>
        Predict tomorrow's move for {ticker}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '16px' }}>
        {TIERS.map(tier => {
          const isSelected = selectedTier === tier.id;
          return (
            <button
              key={tier.id}
              onClick={() => {
                setSelectedTier(tier.id);
                // Auto-submit on click
                setTimeout(() => {
                  if (onSubmit) {
                    setSubmitted(true);
                    onSubmit({
                      ticker,
                      tier: tier.id,
                      mood: selectedMood,
                      timestamp: new Date().toISOString(),
                    });
                  }
                }, 300);
              }}
              style={{
                padding: '12px 6px',
                borderRadius: '12px',
                border: '2px solid ' + (isSelected ? tier.color : tier.borderTint),
                background: isSelected ? tier.bgTint : 'transparent',
                color: isSelected ? tier.color : theme.textMuted,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                boxShadow: isSelected
                  ? (tier.id === 'big_rise' ? '0 0 16px rgba(34, 160, 107, 0.4)'
                    : tier.id === 'big_drop' ? '0 0 16px rgba(207, 68, 68, 0.4)'
                    : tier.id === 'small_rise' ? '0 0 12px rgba(87, 217, 163, 0.3)'
                    : tier.id === 'small_drop' ? '0 0 12px rgba(224, 122, 95, 0.3)'
                    : '0 0 10px rgba(139, 148, 158, 0.25)')
                  : 'none',
              }}
            >
              <span style={{ fontSize: '22px', lineHeight: 1, color: isSelected ? tier.color : theme.textDark, transition: 'color 0.15s ease' }}>
                {tier.icon}
              </span>
              <span style={{ fontSize: '14px', fontWeight: 600 }}>{tier.label}</span>
              <span style={{ fontSize: '11px', fontWeight: 400, opacity: 0.6 }}>{tier.range}</span>
            </button>
          );
        })}
      </div>

      {/* Mood — optional */}
      <div style={{ padding: '10px 14px', background: 'rgba(45,212,191,0.04)', borderRadius: '10px', border: '1px solid ' + theme.border }}>
        <p style={{ color: theme.textDark, fontSize: '13px', margin: '0 0 8px' }}>
          Mood (optional)
        </p>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {MOODS.map(mood => {
            const isSel = selectedMood === mood.id;
            return (
              <button
                key={mood.id}
                onClick={() => setSelectedMood(isSel ? null : mood.id)}
                style={{
                  padding: '5px 10px',
                  borderRadius: '20px',
                  border: '1px solid ' + (isSel ? theme.teal : theme.border),
                  background: isSel ? 'rgba(45, 212, 191, 0.1)' : 'transparent',
                  color: isSel ? theme.teal : theme.textMuted,
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <span style={{ fontSize: '15px' }}>{mood.icon}</span>
                {mood.label}
              </button>
            );
          })}
        </div>
      </div>

      <p style={{ color: theme.textDark, fontSize: '12px', margin: '6px 0 0', opacity: 0.6, textAlign: 'center' }}>
        Tap a direction to predict — results checked tomorrow
      </p>
    </div>
  );
}

export default DailyPrediction;
